import { useDisclosure } from "@chakra-ui/hooks";
import { Modal, ModalOverlay } from "@chakra-ui/modal";
import DivLoader from "components/divloader/DivLoader";
import { useEffect, useState } from "react";
import { MdDelete, MdHighlightOff } from "react-icons/md";
import { Popover, PopoverTrigger, PopoverContent, PopoverHeader, PopoverArrow } from "@chakra-ui/react";

const DeleteModal = ({
  name, id, fetchListData, heading, infoData, message, handleDelete,
}: {
  name: string; id: string; fetchListData: () => void;
  heading: string; message: string; handleDelete: any; infoData?: any;
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isLoading, setIsLoading] = useState<boolean>();
  const [isCloseModal, setIsCloseModal] = useState<boolean>(false);
  const [record, setRecord] = useState<any>({});
  const [popoverOpen, setPopoverOpen] = useState(false);

  useEffect(() => { setRecord(infoData?.row?.original ?? {}); }, [infoData]);

  const handleClose = () => {
    setIsCloseModal(true);
    setTimeout(() => { setIsCloseModal(false); onClose(); }, 300);
  };

  const fullName = [record.first_name?.trim(), record.last_name?.trim()].filter(Boolean).join(" ") || "—";

  return (
    <>
      <Popover trigger="hover" placement="bottom" closeOnBlur  closeOnEsc isOpen={popoverOpen && !isOpen} onOpen={() => setPopoverOpen(true)} onClose={() => setPopoverOpen(false)}>
        <PopoverTrigger>
          <button className="bl-del-trigger" onClick={onOpen} aria-label="Delete" onMouseEnter={() => !isOpen && setPopoverOpen(true)} onMouseLeave={() => setPopoverOpen(false)}>
            <MdDelete size={15} />
          </button>
        </PopoverTrigger>
        <PopoverContent border="1px solid #fecaca" borderRadius="12px" bg="#fff1f2" maxW="160px" p="0" overflow="hidden" zIndex="20">
          <PopoverHeader bg="#ffe4e6" fontSize="12px" fontWeight="700" color="#b91c1c" borderBottom="1px solid #fecaca" px="12px" py="9px">Delete Record</PopoverHeader>
          <PopoverArrow bg="#fff1f2" />
        </PopoverContent>
      </Popover>

      <Modal isOpen={isOpen} onClose={() => {}}>
        <ModalOverlay bg="rgba(15,23,42,0.55)" backdropFilter="blur(4px)" />
        <div className={`bld-panel${isOpen && !isCloseModal ? " bld-panel--open" : " bld-panel--close"}`}>
          <div className="bld-glow" />

          <div className="bld-header">
            <div className="bld-header-brand">
              <div className="bld-header-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/>
                  <path d="M10 11v6"/><path d="M14 11v6"/>
                  <path d="M9 6V4h6v2"/>
                </svg>
              </div>
              <div>
                <h2 className="bld-title">Delete Record</h2>
                <p className="bld-sub">This action is irreversible</p>
              </div>
            </div>
            <button className="bld-close" onClick={handleClose} aria-label="Close">
              <MdHighlightOff size={22} />
            </button>
          </div>

          <div className="bld-body">
            {/* Warning card */}
            <div className="bld-warning">
              <div className="bld-warning-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                  <line x1="12" y1="9" x2="12" y2="13"/>
                  <line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
              </div>
              <div>
                <p className="bld-warning-title">Irreversible Action</p>
                <p className="bld-warning-desc">This blacklist record will be permanently deleted and cannot be recovered.</p>
              </div>
            </div>

            {/* Record details */}
            <div className="bld-record-card">
              <p className="bld-record-label">Record to be deleted</p>
              <div className="bld-record-rows">
                <div className="bld-record-row">
                  <span className="bld-record-key">Name</span>
                  <span className="bld-record-val">{fullName}</span>
                </div>
                <div className="bld-record-row">
                  <span className="bld-record-key">Email</span>
                  <span className="bld-record-val">{record.email?.trim() || "—"}</span>
                </div>
                <div className="bld-record-row">
                  <span className="bld-record-key">BIN + Last 4</span>
                  <span className="bld-record-val bld-record-val--mono">
                    {record.bin?.trim() ? `${record.bin.trim()}${record.last_four?.trim() ? " – " + record.last_four.trim() : ""}` : "—"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="bld-footer">
            <button className="bld-btn-cancel" onClick={handleClose}>Cancel</button>
            <button
              className="bld-btn-delete"
              onClick={() => handleDelete(id)}
              disabled={!!isLoading}
            >
              {isLoading ? <span className="bld-spinner" /> : (
                <><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/></svg>Delete Permanently</>
              )}
            </button>
          </div>
        </div>
      </Modal>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

        .bl-del-trigger { width:30px; height:30px; border-radius:8px; background:#fff7f7; border:1.5px solid #fecaca; color:#dc2626; cursor:pointer; display:flex; align-items:center; justify-content:center; transition:all .15s; }
        .bl-del-trigger:hover { background:#fee2e2; border-color:#fca5a5; }

        .bld-panel { font-family:'Plus Jakarta Sans',sans-serif; position:fixed; top:0; right:0; bottom:0; width:100%; max-width:520px; background:#fff; z-index:1004; display:flex; flex-direction:column; box-shadow:-12px 0 60px rgba(15,23,42,.18); transition:transform .35s cubic-bezier(0.16,1,0.3,1); overflow:hidden; }
        .bld-panel--open  { transform:translateX(0); }
        .bld-panel--close { transform:translateX(100%); }

        .bld-glow { height:3px; flex-shrink:0; background:linear-gradient(90deg,#7f1d1d,#dc2626,#ef4444,#dc2626,#7f1d1d); background-size:200% 100%; animation:bldGlow 3s linear infinite; }
        @keyframes bldGlow { 0%{background-position:0%} 100%{background-position:200%} }

        .bld-header { padding:15px 26px; background:linear-gradient(135deg,#7f1d1d 0%,#b91c1c 60%,#dc2626 100%); display:flex; align-items:center; justify-content:space-between; flex-shrink:0; }
        .bld-header-brand { display:flex; align-items:center; gap:13px; }
        .bld-header-icon { width:42px; height:42px; border-radius:12px; background:rgba(255,255,255,.15); border:1.5px solid rgba(255,255,255,.25); display:flex; align-items:center; justify-content:center; flex-shrink:0; }
        .bld-title { font-size:16px; font-weight:800; color:#fff; letter-spacing:-.3px; margin-bottom:2px; line-height:1; }
        .bld-sub { font-size:12px; color:rgba(255,255,255,.55); font-weight:500; }
        .bld-close { background:none; border:none; cursor:pointer; color:rgba(255,255,255,.65); display:flex; align-items:center; transition:color .15s; }
        .bld-close:hover { color:#fff; }

        .bld-body { flex:1; padding:24px 26px; display:flex; flex-direction:column; gap:16px; overflow-y:auto; }

        .bld-warning { display:flex; gap:14px; background:linear-gradient(135deg,#fff7f7,#fee2e2); border:1.5px solid #fecaca; border-radius:13px; padding:16px 18px; }
        .bld-warning-icon { width:44px; height:44px; border-radius:11px; background:#fff; border:1.5px solid #fecaca; display:flex; align-items:center; justify-content:center; color:#dc2626; flex-shrink:0; }
        .bld-warning-title { font-size:14px; font-weight:700; color:#991b1b; margin-bottom:5px; }
        .bld-warning-desc { font-size:13px; color:#b91c1c; font-weight:500; line-height:1.5; }

        .bld-record-card { background:#f8fafc; border:1.5px solid #e2e8f0; border-radius:13px; overflow:hidden; }
        .bld-record-label { padding:10px 16px 8px; font-size:11px; font-weight:800; color:#94a3b8; text-transform:uppercase; letter-spacing:0.7px; border-bottom:1px solid #f1f5f9; }
        .bld-record-rows { display:flex; flex-direction:column; }
        .bld-record-row { display:flex; align-items:center; justify-content:space-between; padding:11px 16px; border-bottom:1px solid #f1f5f9; }
        .bld-record-row:last-child { border-bottom:none; }
        .bld-record-key { font-size:12px; font-weight:700; color:#94a3b8; }
        .bld-record-val { font-size:13px; font-weight:600; color:#0f172a; }
        .bld-record-val--mono { font-family:'Fira Code',monospace; letter-spacing:0.5px; }

        .bld-footer { padding:14px 26px; border-top:1.5px solid #f1f5f9; background:#fafbfc; display:flex; align-items:center; justify-content:flex-end; gap:10px; flex-shrink:0; }
        .bld-btn-cancel { font-family:'Plus Jakarta Sans',sans-serif; font-size:13px; font-weight:600; color:#64748b; padding:9px 16px; border-radius:10px; border:1.5px solid #e2e8f0; background:#fff; cursor:pointer; transition:all .15s; }
        .bld-btn-cancel:hover { background:#f1f5f9; }
        .bld-btn-delete { display:inline-flex; align-items:center; gap:7px; font-family:'Plus Jakarta Sans',sans-serif; font-size:13.5px; font-weight:700; color:#fff; padding:10px 18px; border-radius:10px; border:none; background:linear-gradient(135deg,#991b1b,#dc2626); cursor:pointer; box-shadow:0 4px 14px rgba(220,38,38,.28); transition:all .15s; }
        .bld-btn-delete:hover { transform:translateY(-1px); box-shadow:0 8px 24px rgba(220,38,38,.36); }
        .bld-btn-delete:disabled { opacity:.5; cursor:not-allowed; transform:none; }
        .bld-spinner { display:inline-block; width:14px; height:14px; border:2px solid rgba(255,255,255,.35); border-top-color:#fff; border-radius:50%; animation:bldGlow1 .65s linear infinite; }
        @keyframes bldGlow1 { to{transform:rotate(360deg)} }

        @media (max-width:640px) { .bld-panel { max-width:100%; } }
      `}</style>
    </>
  );
};

export default DeleteModal;